
import { inject, Injectable } from '@angular/core';
import { CustomTextHelper } from '@workflow/helper/custom-text-helper/custom-text-helper.class';
import { CardHelper } from './card-helper.class';
import { PATH_ITERATION_SYMBOL, PATH_PAGE_ITERATION_SYMBOL, FIELD_ITERATION_SYMBOL } from './stateful-value.class';
import { ValidationBase } from './validation-base.class';
import { dependencies } from './workflow.dependency';
import { WorkflowCard, PrimitiveType } from './workflow.type';
import { FieldInstance, FieldService } from './field.service';
import { StatefulService } from '@app/service/stateful.service';
import { registerDevtoolsState } from '@shared/service/devtools/devtools-state-registry.service';

@Injectable({
  providedIn: 'root'
})
export class CardService {
  private instances: { [id: string]: CardInstance } = {};
  private readonly fieldService = inject(FieldService);
  private readonly statefulService = inject(StatefulService);

  constructor() {
    registerDevtoolsState(this, 'CardService');
  }

  public createCard(id: string, flowId: string, iterationId?: number, pageIterationId?: number) {
    const tempId = id;

    if (iterationId != null) {
      if (tempId.indexOf(PATH_ITERATION_SYMBOL) < 0) {
        throw new Error(`Iteration ID provided for non-array card ID: ${tempId} : ${iterationId}`);
      }
      id = id.replace(PATH_ITERATION_SYMBOL, String(iterationId));
    } else if (tempId.indexOf(PATH_ITERATION_SYMBOL) > -1) {
      throw new Error(`Array card ID requires iteration ID: ${tempId}`);
    }
    if (pageIterationId != null) {
      if (tempId.indexOf(PATH_PAGE_ITERATION_SYMBOL) < 0) {
        throw new Error(`Page iteration ID provided for non-array card ID: ${tempId} : ${pageIterationId}`);
      }
      id = id.replace(PATH_PAGE_ITERATION_SYMBOL, String(pageIterationId));
    } else if (tempId.indexOf(PATH_PAGE_ITERATION_SYMBOL) > -1) {
      throw new Error(`Array field ID requires page iteration ID: ${tempId}`);
    }
    const instanceId = `${flowId}/${id}`;
    // enforcing unique instances per id
    if (this.instances[instanceId]) {
      return this.instances[instanceId];
    }

    const newInstance = new CardInstance(this.fieldService, this.statefulService, id, flowId, tempId, iterationId, pageIterationId);

    this.instances[instanceId] = newInstance;

    return newInstance;
  }

  /**
   * Below method should be called to clear Card
   * Make sure this method called only once
   */
  public clear(): void {
    for (const [id] of Object.entries(this.instances)) {
      delete this.instances[id];
    }
  }

  public ensureConfig(configId: string): void {
    const config = dependencies.config.cards[configId];
    if (!config) {
      return;
    }

    // ensure consistency with IDs
    config.id = configId;
  }

  /**
   * Test configuration values for validity + set sane defaults (e.g. empty object where missing, adding implicit IDs, etc)
   * Throws errors on integration fails
   * @param id card ID from `workflow-cards.json`
   */
  public test(id: string): void {
    const config = dependencies.config.cards[id];

    // logic functions
    dependencies.logicService.testConfig(config, 'card', 'showRemove', id);
    dependencies.logicService.testConfig(config, 'card', 'visible', id);

    const loopType = id.indexOf(PATH_ITERATION_SYMBOL) > -1;
    if (loopType) {
      dependencies.logicService.testConfig(config, 'card', 'iterationMax', id);
    }

    if (config.array && !loopType) {
      throw new Error(`Array property can only be added to loop type cards. Card ID ${id} does not have $`);
    }

    // fields syntax and integrity with `workflow-fields.json`
    if (!Array.isArray(config.fields)) {
      throw new Error(`The "fields" property is missing or not an array on card ${id}: ${JSON.stringify(config.fields)}`);
    }
    for (const fieldId of config.fields) {
      if (typeof fieldId !== 'string' || !fieldId) {
        throw new Error(`A fieldId is invalid on card ${id}: ${JSON.stringify(fieldId)}`);
      }
      if (!dependencies.config.fields[fieldId]) {
        throw new Error(`A fieldId referenced on card ${id} does not exist in workflow-fields: "${fieldId}"`);
      }
    }
  }
}

// ********************************************************

/**
 * Card specific logic and configuration store
 */
export class CardInstance extends ValidationBase {
  /**
   * Card field helper instance
   */
  readonly #cardHelper: CardHelper;

  /**
   * Fix array reference for the `.fields` getter
   */
  readonly #fields: FieldInstance[] = [];

  public readonly instanceId: string;
  /**
   * card config as specified at `workflow-fields.json`
   */
  public readonly config: WorkflowCard;

  /**
   * Create or return a unique Card
   * @param id config id (as set in `workflow-cards.json`)
   * @param iterationId optional iteration ID (for loops)
   * @param pageIterationId optional page iteration ID (for page loops)
   */
  constructor(
    private readonly fieldService: FieldService,
    private readonly statefulService: StatefulService,
    public readonly id: string,
    public readonly flowId: string,
    public readonly originalId: string,
    public readonly iterationId?: number,
    public readonly pageIterationId?: number
  ) {
    super();
    this.id = id;

    this.instanceId = `${flowId}/${this.id}`;
    const instanceConfigId = `${flowId}/${originalId}`;

    if (dependencies.config.workflowSpecificCards[instanceConfigId]) {
      this.config = { ...dependencies.config.cards[originalId], ...dependencies.config.workflowSpecificCards[instanceConfigId] };
    } else {
      this.config = dependencies.config.cards[originalId];
    }
    this.#cardHelper = new CardHelper(this);
  }

  /**
   * Whether card should be enabled
   */
  public get enabled(): boolean {
    return dependencies.logicService.evaluate(this.config.enabled, { card: this }, true);
  }

  /**
   * Whether edit link should be disabled in review page
   */
  public get disableEditInReview(): boolean {
    if (!this.config.disableEditInReview) {
      return false;
    }

    return dependencies.logicService.evaluate(this.config.disableEditInReview, { card: this }, true);
  }

  /**
   * return list of fields that are touched in the card, by default it filters visible fields
   */
  public get reviewableFields(): FieldInstance[] {
    return this.fields.filter((f) => f.isReviewableField);
  }

  /**
   * Ordered list of field objects for the card
   */
  public get fields(): FieldInstance[] {
    this.#fields.length = 0;

    this.config.fields.forEach((fieldId) => {
      if (fieldId.includes(FIELD_ITERATION_SYMBOL)) {
        const fieldConfig = dependencies.config.fields[fieldId];
        const size = this.statefulService.arraySize(fieldConfig.iterativeArray, this.pageIterationId);
        for (let i = 0; i < size; i++) {
          this.#fields.push(
            this.fieldService.createField(fieldId.replace(FIELD_ITERATION_SYMBOL, PATH_PAGE_ITERATION_SYMBOL), this.flowId, null, i)
          );
        }
      } else {
        this.#fields.push(this.fieldService.createField(fieldId, this.flowId, this.iterationId, this.pageIterationId));
      }
    });

    return this.#fields;
  }

  /**
   * @inheritdoc
   */
  public hasValidation(fnName: string): PrimitiveType[] {
    return dependencies.logicService.hasFunction(fnName, this.config.validation);
  }

  /**
   * Page proxy provides helper functions for page fields and validations
   */
  public get helper(): CardHelper {
    return this.#cardHelper;
  }

  public get hideWhenEmpty(): boolean {
    return this.config.hideWhenEmpty || false;
  }

  /**
   * Whether card should be visible
   */
  public get showRemove(): boolean {
    return (
      this.config.showRemove &&
      dependencies.logicService.evaluate(this.config.showRemove, { card: this }, true) &&
      (!this.config.arrayMinimum || this.config.arrayMinimum <= this.iterationId)
    );
  }

  /**
   * Whether card should be visible
   */
  public get visible(): boolean {
    const isVisible = dependencies.logicService.evaluate(this.config.visible, { card: this }, true);
    if (isVisible && this.config.hideWhenEmpty) {
      return this.fields.findIndex((t) => t.visible) !== -1;
    }
    return isVisible;
  }

  /**
   * Whether card should be visible
   */
  public get preview(): boolean {
    if (!this.config.preview) return false;
    return dependencies.logicService.evaluate(this.config.preview, { card: this }, true);
  }

  private getArrayId(): string {
    return this.pageIterationId == null
      ? this.config.array
      : this.config.array.replace(PATH_PAGE_ITERATION_SYMBOL, String(this.pageIterationId));
  }

  private removeFromArray(): void {
    const id = this.getArrayId();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.statefulService.arrayRemove(id, this.iterationId, (cb) => {
      this.afterRemove();
    });
  }

  /**
   * Whether card should be visible
   */
  public remove(removeArray = false): void {
    if (this.config.onRemove) {
      dependencies.logicService.evaluate(this.config.onRemove, { card: this }, true);
      if (removeArray) {
        this.removeFromArray();
      } else {
        this.afterRemove();
      }
    } else {
      this.removeFromArray();
    }
  }

  /**
   * Trigger revalidation
   */
  public revalidate(): string[] {
    if (this.enabled && this.visible) {
      return this.spreadValidation(dependencies.logicService.evaluate(this.config.validation, { card: this }, 'collect', this.iterationId));
    }
    return [];
  }

  private afterRemove(): void {
    if (this.config.afterRemove) {
      dependencies.logicService.evaluate(this.config.afterRemove, { card: this });
    }
  }

  /**
   * Custom field title or default title
   */
  public get customTitleOrDefault(): string {
    return CustomTextHelper.customTextOrDefault(this.config.titleFn, { card: this }, this.title, this.iterationId, this.pageIterationId);
  }

  public get summaryTitle(): string {
    return this.config['summary-title'];
  }

  public get title(): string {
    return dependencies.stringVariableService.parse(this.config.title, this.iterationId, this.pageIterationId) as string;
  }
  /** Tooltip for Cards subtitle */
  public get tooltip(): string {
    return CustomTextHelper.customTextOrDefault(
      this.config.tooltipFn,
      { card: this },
      this.config.tooltip,
      this.iterationId,
      this.pageIterationId
    );
  }

  public get hideInReview(): boolean {
    if (!this.config.hideInReview) {
      return false;
    }

    if (typeof this.config.hideInReview === 'boolean') {
      return this.config.hideInReview;
    }

    return dependencies.logicService.evaluate(this.config.hideInReview, { card: this }, true, null, this.pageIterationId);
  }

  /**
   * Adds an optional automation ID as defined in the workflow.jsons
   * Will fall back to the field ID if no automation ID is defined
   * @returns automation Id
   */
  public get automationId(): string {
    return this.config?.automationId ? this.config.automationId : this.id;
  }
}
